import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Text, ActionButton, Icon } from "../custom-components";
import { BsSearch, BsThreeDotsVertical } from "react-icons/bs";

import { IcEditPen, IcTrash, IcSort, IcGroup, IcFilter, IcDownload, IcInfo, IcDocumentViewer } from "../custom-components/Icon";
import { listUsers, deleteUser, searchBusiness, searchRoles } from "../store/actions/CommonAction";
import { toast } from 'react-toastify';
import "../styles/businessGroups.css";
import "../styles/pageConfiguration.css";

const Users = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [users, setUsers] = useState([]);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const [openDropdownId, setOpenDropdownId] = useState(null);

  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownId(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const sortedUsers = React.useMemo(() => {
    if (!sortColumn) return users;

    return [...users].sort((a, b) => {
      const aValue = a[sortColumn] || '';
      const bValue = b[sortColumn] || '';

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [users, sortColumn, sortDirection]);


  useEffect(() => {
    const fetchUsersAndDetails = async () => {
      try {
        const usersResponse = await dispatch(listUsers());
        const fetchedUsers = usersResponse || [];

        const usersWithDetails = await Promise.all(
          fetchedUsers.map(async (user) => {
            const rolesWithDetails = await Promise.all(
              (user.roles || []).map(async (role) => {
                let businessName = 'N/A';
                let roleName = 'N/A';

                // Fetch Business Group Name
                try {
                  const businessResponse = await dispatch(searchBusiness(role.businessId));
                  // Handle both array response (searchList) and single object response
                  businessName = businessResponse?.searchList?.[0]?.name ||
                    businessResponse?.name ||
                    'N/A';
                } catch (error) {
                  console.error("Failed to fetch business details for", role.businessId, ":", error);
                }

                // Fetch Role Name
                try {
                  const roleResponse = await dispatch(searchRoles(role.roleId));
                  roleName = roleResponse?.role || 'N/A';
                } catch (error) {
                  console.error("Failed to fetch role details for", role.roleId, ":", error);
                }

                return { ...role, businessName, roleName };
              })
            );
            return { ...user, roles: rolesWithDetails };
          })
        );
        setUsers(usersWithDetails);
      } catch (error) {
        console.error("Failed to fetch users or their details:", error);
        setUsers([]);
      }
    };
    fetchUsersAndDetails();
  }, [dispatch]);


  const handleAddUserClick = () => {
    navigate("/addUser");
  };

  const handleUpdate = (e, userId) => {
    e.stopPropagation(); // Prevent row from expanding when clicking icon
    navigate(`/updateUser/${userId}`);
  };

  const handleDelete = async (e, userId) => {
    e.stopPropagation(); // Prevent row from expanding when clicking icon
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await dispatch(deleteUser(userId));
        setUsers(users.filter(user => user.userId !== userId));
        toast.success("User deleted successfully!");
      } catch (error) {
        toast.error("An error occurred while deleting the user.");
      }
    }
  };

  const handleView = (e, userId) => {
    e.stopPropagation(); // Prevent row from expanding when clicking icon
    navigate(`/addUser?mode=view&id=${userId}`);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (column) => {
    return <Icon ic={<IcSort />} size="small" color="black" />;
  };

  return (
    <div className="configurePage">
      <div className="bs-page">
        <div className="bs-header-container">
          <div className="bs-header-left">
            <div className="bs-header-icon-container">
              <Icon ic={<IcGroup height={24} width={24} />} color="primary_60" />
            </div>
            <div className="bs-header-text inline-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text appearance="heading-s" color="primary-grey-100">
                Users & Teams
              </Text>
              <div className="custom-header-tooltip-container" style={{ display: 'flex' }}>
                <div className="custom-header-tooltip-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                </div>
                <div className="custom-header-tooltip-text" style={{ width: '280px', fontWeight: 'normal' }}>
                  Manage users, assign roles and control access to the consent management platform.
                </div>
              </div>
            </div>
          </div>
          <div className="bs-header-right" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              onClick={handleAddUserClick}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#3B82F6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(59,130,246,0.3)', whiteSpace: 'nowrap' }}
            >
              + Add User
            </button>
          </div>
        </div>

        <div className="summary-cards-container">
          <div className="summary-card">
            <div className="summary-icon blue">
              <Icon ic={<IcGroup />} />
            </div>
            <div className="summary-info">
              <span className="summary-value">{users.length || 0}</span>
              <span className="summary-label">Total Users</span>
              <span className="summary-subtext">All system users</span>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon green">
              <Icon ic={<IcGroup />} />
            </div>
            <div className="summary-info">
              <span className="summary-value">{users.length || 0}</span>
              <span className="summary-label">Active Users</span>
              <span className="summary-subtext">Currently have access</span>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon yellow">
              <Icon ic={<IcGroup />} />
            </div>
            <div className="summary-info">
              <span className="summary-value">0</span>
              <span className="summary-label">Inactive Users</span>
              <span className="summary-subtext">Access revoked</span>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon purple">
              <Icon ic={<IcGroup />} />
            </div>
            <div className="summary-info">
              <span className="summary-value">
                {users.reduce((acc, user) => acc + (user.roles?.length || 0), 0)}
              </span>
              <span className="summary-label">Assigned Roles</span>
              <span className="summary-subtext">Across all users</span>
            </div>
          </div>
        </div>

        <div className="bs-content">
          <div className="business-table-card" style={{ minHeight: '350px', overflow: 'visible' }}>
            <div className="business-table-container" style={{ overflow: 'visible' }}>
              <table className="business-table">
                <thead>
                  <tr>
                    <th style={{ width: '4%' }}>
                      <input type="checkbox" style={{ width: '16px', height: '16px' }} />
                    </th>
                    <th style={{ width: '4%' }}>#</th>
                    <th onClick={() => handleSort('username')} style={{ cursor: 'pointer' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>User Name</span>
                        {renderSortIcon('username')}
                      </div>
                    </th>
                    <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Email / Mobile</span>
                        {renderSortIcon('email')}
                      </div>
                    </th>
                    <th onClick={() => handleSort('designation')} style={{ cursor: 'pointer' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Designation</span>
                        {renderSortIcon('designation')}
                      </div>
                    </th>
                    <th>
                      <span>Roles</span>
                    </th>
                    <th>
                      <span>Status</span>
                    </th>
                    <th>
                      <span>Created On</span>
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers && sortedUsers.length > 0 ? (
                    sortedUsers.map((user, index) => {
                      const initials = user.username ? user.username.substring(0, 2).toUpperCase() : "U";
                      const firstRole = (user.roles && user.roles.length > 0) ? user.roles[0].roleName : null;
                      const moreRoles = (user.roles && user.roles.length > 1) ? `+${user.roles.length - 1}` : null;
                      
                      return (
                        <tr key={user.userId}>
                          <td>
                            <input type="checkbox" style={{ width: '16px', height: '16px' }} />
                          </td>
                          <td>{index + 1}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#2563EB', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 'bold' }}>
                                {initials}
                              </div>
                              <span style={{ fontWeight: 600 }}>{user.username || "N/A"}</span>
                            </div>
                          </td>
                          <td>{user.email || user.mobile || "N/A"}</td>
                          <td>
                            {user.designation ? (
                               <span style={{ padding: '4px 8px', backgroundColor: '#F3F4F6', borderRadius: '12px', fontSize: '12px', color: '#4B5563' }}>{user.designation}</span>
                            ) : (
                               <span style={{ padding: '4px 8px', backgroundColor: '#F3F4F6', borderRadius: '12px', fontSize: '12px', color: '#9CA3AF' }}>Not Assigned</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {firstRole ? (
                                <>
                                  <span style={{ padding: '4px 10px', backgroundColor: '#EFF6FF', color: '#2563EB', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                                    {firstRole === 'N/A' ? 'User' : firstRole}
                                  </span>
                                  {moreRoles && (
                                    <span style={{ padding: '4px 8px', backgroundColor: '#F3F4F6', color: '#4B5563', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                                      {moreRoles}
                                    </span>
                                  )}
                                </>
                              ) : (
                                <span style={{ padding: '4px 10px', backgroundColor: '#F3F4F6', color: '#9CA3AF', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                                  No Role
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className="status-badge active">
                              <span className="status-dot"></span> Active
                            </span>
                          </td>
                          <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-GB').replace(/\//g, '-') : "24-09-2026"}</td>
                          <td>
                            <div className="action-icons" style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                              <button
                                className="icon-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenDropdownId(openDropdownId === user.userId ? null : user.userId);
                                }}
                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px' }}
                              >
                                <BsThreeDotsVertical size={18} color="#64748B" />
                              </button>

                              {openDropdownId === user.userId && (
                                <div className="action-dropdown-menu" style={{
                                  position: 'absolute',
                                  right: '10px',
                                  top: '100%',
                                  background: 'white',
                                  border: '1px solid #E2E8F0',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                                  zIndex: 99,
                                  minWidth: '150px',
                                  padding: '4px 0',
                                  textAlign: 'left'
                                }}>
                                  <div 
                                    onClick={(e) => { e.stopPropagation(); handleUpdate(e, user.userId); setOpenDropdownId(null); }}
                                    style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', color: '#0F172A' }}
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F1F5F9'}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                  >
                                    <IcEditPen height={16} width={16} />
                                    Edit User
                                  </div>
                                  <div 
                                    onClick={(e) => { e.stopPropagation(); handleView(e, user.userId); setOpenDropdownId(null); }}
                                    style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', color: '#0F172A' }}
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F1F5F9'}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                  >
                                    <IcDocumentViewer height={16} width={16} color="#0F172A" />
                                    View User
                                  </div>
                                  {!user.roles?.some(role => role.roleName && role.roleName.toLowerCase() === 'admin') && (
                                    <div 
                                      onClick={(e) => { e.stopPropagation(); handleDelete(e, user.userId); setOpenDropdownId(null); }}
                                      style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', color: '#EF4444' }}
                                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#FEE2E2'}
                                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                      <IcTrash height={16} width={16} color="#EF4444" />
                                      Delete User
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="9" style={{ textAlign: "center", padding: "20px" }}>No users found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="business-pagination">
              <Text appearance="body-xs" color="primary-grey-60">
                Showing 1 to {sortedUsers?.length || 0} of {sortedUsers?.length || 0} users
              </Text>
              <div className="pagination-controls">
                <button className="page-btn disabled">&lt;</button>
                <button className="page-btn active">1</button>
                <button className="page-btn disabled">&gt;</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;